import React, { useState } from 'react';
import { ToolType } from '../types';
import { RateCardStudio } from '../components/tools/RateCardStudio';
import { ScopeShield } from '../components/tools/ScopeShield';
import { TeardownLab } from '../components/tools/TeardownLab';
import { AgreementArmor } from '../components/tools/AgreementArmor';
import { ToolCheckoutModal } from '../components/ToolCheckoutModal';

interface ToolsHubViewProps {
  unlockedTools?: string[];
  onUnlockTool?: (toolId: string, email: string, price: number, planName: string) => void;
  stripePaymentLink?: string;
}

const TOOL_TITLES: Record<ToolType, string> = {
  ratecard: 'Rate Card Studio',
  scopeshield: 'Scope Shield',
  teardown: 'Teardown Lab',
  agreement: 'Agreement Armor',
};

export const ToolsHubView: React.FC<ToolsHubViewProps> = ({
  unlockedTools = [],
  onUnlockTool = () => {},
  stripePaymentLink,
}) => {
  const [activeTool, setActiveTool] = useState<ToolType>('ratecard');
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<'single' | 'bundle'>('single');

  const isAllAccessUnlocked = unlockedTools.includes('all-access-pass');
  const isToolUnlocked = (tool: ToolType) => isAllAccessUnlocked || unlockedTools.includes(tool);

  const handleOpenCheckout = (plan?: 'single' | 'bundle') => {
    setCheckoutPlan(plan || 'single');
    setIsCheckoutOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Suite Status Bar */}
      <div className="no-print">
        {isAllAccessUnlocked ? (
          <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between text-xs font-mono text-emerald-400">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="font-bold">STUDIO ALL-ACCESS PASS ACTIVE:</span> All 4 tools unlocked for commercial, watermark-free vector PDF exports.
            </div>
            <span className="text-[10px] uppercase font-bold tracking-widest bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/30">
              UNLIMITED PRO
            </span>
          </div>
        ) : (
          <div className="p-4 rounded-xl blueprint-card border border-[#232736] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-mono">
            <div>
              <span className="text-white font-bold font-tech text-sm block sm:inline mr-2">
                The Client Deliverables Studio Suite:
              </span>
              <span className="text-[#8b92a5]">
                Generate client-ready rate cards, change orders, audits, and agreements in under 60 seconds.
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-zinc-500 hidden sm:inline">Free Preview Mode</span>
              <button
                onClick={() => handleOpenCheckout('bundle')}
                className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-tech font-bold text-xs transition shadow-[0_0_15px_rgba(16,185,129,0.2)] flex items-center gap-1.5"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
                GET ALL-ACCESS PASS ($29)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tool Navigation Tabs */}
      <div className="blueprint-card rounded-2xl p-2 border border-[#232736] flex flex-wrap gap-2 no-print bg-[#10121a]">
        <button
          onClick={() => setActiveTool('ratecard')}
          className={`flex-1 min-w-[200px] py-3 px-4 rounded-xl font-mono text-xs transition text-left flex items-center justify-between ${
            activeTool === 'ratecard'
              ? 'bg-sky-500/20 border border-sky-400/50 text-white font-bold shadow-[0_0_15px_rgba(56,189,248,0.2)]'
              : 'text-[#8b92a5] hover:text-white hover:bg-[#181b26] border border-transparent'
          }`}
        >
          <div>
            <div className="text-[10px] text-sky-400 uppercase tracking-widest font-bold">TOOL 01</div>
            <div className="font-tech text-sm text-white">Rate Card Studio</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20">
            {isToolUnlocked('ratecard') ? 'PRO ✓' : 'PRICING'}
          </span>
        </button>

        <button
          onClick={() => setActiveTool('scopeshield')}
          className={`flex-1 min-w-[200px] py-3 px-4 rounded-xl font-mono text-xs transition text-left flex items-center justify-between ${
            activeTool === 'scopeshield'
              ? 'bg-amber-500/20 border border-amber-400/50 text-white font-bold shadow-[0_0_15px_rgba(245,158,11,0.2)]'
              : 'text-[#8b92a5] hover:text-white hover:bg-[#181b26] border border-transparent'
          }`}
        >
          <div>
            <div className="text-[10px] text-amber-400 uppercase tracking-widest font-bold">TOOL 02</div>
            <div className="font-tech text-sm text-white">Scope Shield</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
            {isToolUnlocked('scopeshield') ? 'PRO ✓' : 'DEFENSE'}
          </span>
        </button>

        <button
          onClick={() => setActiveTool('teardown')}
          className={`flex-1 min-w-[200px] py-3 px-4 rounded-xl font-mono text-xs transition text-left flex items-center justify-between ${
            activeTool === 'teardown'
              ? 'bg-red-500/20 border border-red-400/50 text-white font-bold shadow-[0_0_15px_rgba(239,68,68,0.2)]'
              : 'text-[#8b92a5] hover:text-white hover:bg-[#181b26] border border-transparent'
          }`}
        >
          <div>
            <div className="text-[10px] text-red-400 uppercase tracking-widest font-bold">TOOL 03</div>
            <div className="font-tech text-sm text-white">Teardown Lab</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/20">
            {isToolUnlocked('teardown') ? 'PRO ✓' : 'AUDITS'}
          </span>
        </button>

        <button
          onClick={() => setActiveTool('agreement')}
          className={`flex-1 min-w-[200px] py-3 px-4 rounded-xl font-mono text-xs transition text-left flex items-center justify-between ${
            activeTool === 'agreement'
              ? 'bg-emerald-500/20 border border-emerald-400/50 text-white font-bold shadow-[0_0_15px_rgba(16,185,129,0.2)]'
              : 'text-[#8b92a5] hover:text-white hover:bg-[#181b26] border border-transparent'
          }`}
        >
          <div>
            <div className="text-[10px] text-emerald-400 uppercase tracking-widest font-bold">TOOL 04</div>
            <div className="font-tech text-sm text-white">Agreement Armor</div>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
            {isToolUnlocked('agreement') ? 'PRO ✓' : 'CONTRACTS'}
          </span>
        </button>
      </div>

      {/* Render Active Tool */}
      <div className="transition-all duration-200">
        {activeTool === 'ratecard' && (
          <RateCardStudio
            isUnlocked={isToolUnlocked('ratecard')}
            onOpenCheckout={handleOpenCheckout}
          />
        )}
        {activeTool === 'scopeshield' && (
          <ScopeShield
            isUnlocked={isToolUnlocked('scopeshield')}
            onOpenCheckout={handleOpenCheckout}
          />
        )}
        {activeTool === 'teardown' && (
          <TeardownLab
            isUnlocked={isToolUnlocked('teardown')}
            onOpenCheckout={handleOpenCheckout}
          />
        )}
        {activeTool === 'agreement' && (
          <AgreementArmor
            isUnlocked={isToolUnlocked('agreement')}
            onOpenCheckout={handleOpenCheckout}
          />
        )}
      </div>

      {/* Gated Checkout Modal */}
      <ToolCheckoutModal
        toolId={activeTool}
        toolTitle={TOOL_TITLES[activeTool]}
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onUnlock={onUnlockTool}
        stripePaymentLink={stripePaymentLink}
        initialPlan={checkoutPlan}
      />
    </div>
  );
};
